import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { DEFAULT_CONTENT_LANG_OPTIONS } from '../../config/customConfig';
import { switchAppLanguage } from '../../util/genericHelpers';

export const CHANGE_LANGUAGE_REQUEST = 'app/LanguagePreferencesPage/CHANGE_LANGUAGE_REQUEST';
export const CHANGE_LANGUAGE_SUCCESS = 'app/LanguagePreferencesPage/CHANGE_LANGUAGE_SUCCESS';
export const CHANGE_LANGUAGE_ERROR = 'app/LanguagePreferencesPage/CHANGE_LANGUAGE_ERROR';
export const CHANGE_LANGUAGE_CLEAR = 'app/LanguagePreferencesPage/CHANGE_LANGUAGE_CLEAR';

const initialState = {
  changeLanguageError: null,
  changeLanguageInProgress: false,
  languageChanged: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CHANGE_LANGUAGE_REQUEST:
      return {
        ...state,
        changeLanguageInProgress: true,
        changeLanguageError: null,
        languageChanged: false,
      };
    case CHANGE_LANGUAGE_SUCCESS:
      return { ...state, changeLanguageInProgress: false, languageChanged: true };
    case CHANGE_LANGUAGE_ERROR:
      return { ...state, changeLanguageInProgress: false, changeLanguageError: payload };
    case CHANGE_LANGUAGE_CLEAR:
      return { ...initialState };
    default:
      return state;
  }
}

export const changeLanguageRequest = () => ({ type: CHANGE_LANGUAGE_REQUEST });
export const changeLanguageSuccess = () => ({ type: CHANGE_LANGUAGE_SUCCESS });
export const changeLanguageError = error => ({
  type: CHANGE_LANGUAGE_ERROR,
  payload: error,
  error: true,
});
export const changeLanguageClear = () => ({ type: CHANGE_LANGUAGE_CLEAR });

const VALID_LANGUAGE_KEYS = DEFAULT_CONTENT_LANG_OPTIONS.map(option => option.key).filter(Boolean);

const normalizeLanguageKey = value => {
  if (typeof value !== 'string') return null;

  const normalizedValue = value.trim().toLowerCase();
  const languageKeyMap = {
    gr: 'gr',
    el: 'gr',
    en: 'en',
    de: 'de',
    ar: 'ar',
  };

  return languageKeyMap[normalizedValue] || null;
};

const normalizeLanguageList = values => {
  if (!Array.isArray(values)) return undefined;

  return values.reduce((acc, value) => {
    const normalizedValue = normalizeLanguageKey(value);
    if (
      normalizedValue &&
      VALID_LANGUAGE_KEYS.includes(normalizedValue) &&
      !acc.includes(normalizedValue)
    ) {
      acc.push(normalizedValue);
    }
    return acc;
  }, []);
};

export const changeLanguage = params => (dispatch, getState, sdk) => {
  dispatch(changeLanguageRequest());

  const currentProtectedData =
    getState()?.user?.currentUser?.attributes?.profile?.protectedData || {};
  const backendLanguageCode =
    normalizeLanguageKey(params?.uiLanguage || params?.preferredLang) ||
    normalizeLanguageKey(currentProtectedData?.preferredLang) ||
    'en';
  const submittedContentLanguages = normalizeLanguageList(params?.preferredContentLanguages);
  const preferredContentLanguages =
    submittedContentLanguages === undefined
      ? normalizeLanguageList(currentProtectedData?.preferredContentLanguages) || []
      : submittedContentLanguages;

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.currentUser
    .updateProfile(
      { protectedData: { preferredLang: backendLanguageCode, preferredContentLanguages } },
      queryParams
    )
    .then(response => {
      dispatch(changeLanguageSuccess());

      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];

      dispatch(currentUserShowSuccess(currentUser));

      const languageToLocaleMap = {
        gr: 'el',
        en: 'en',
        de: 'de',
        ar: 'ar',
      };

      const newLocale = languageToLocaleMap[backendLanguageCode];
      if (newLocale && typeof window !== 'undefined') {
        switchAppLanguage({
          locale: newLocale,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        });
      }
    })
    .catch(e => {
      dispatch(changeLanguageError(storableError(e)));
      throw e;
    });
};
